import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate, tl, stat } from "@/lib/i18nFormat";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, LoadingBlock, EmptyState, StatusPill } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { LifeBuoy, Plus, ShieldCheck, BarChart3, Settings, Ticket, AlertTriangle } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const STATUS_TONE: Record<string, string> = { new: "warn", triaged: "info", assigned: "info", in_progress: "info", waiting_on_requester: "inactive", waiting_on_third_party: "inactive", resolved: "good", closed: "good", cancelled: "inactive", reopened: "warn" };
const TARGET_TONE: Record<string, string> = { met: "good", on_track: "info", at_risk: "warn", breached: "critical", none: "inactive" };
const label = (s?: string) => (s || "").replace(/_/g, " ");

export default function HelpDesk() {
  const {
    t
  } = useI18n();

  const { modules, hasPerm } = useAuth();
  const canScope = hasPerm("support.view_scope");
  const canAdmin = hasPerm("support.admin");
  const canExport = hasPerm("support.export");
  const [tab, setTab] = useState("my");
  if (modules.support === false) {
    return <div><PageHeader title="Help Desk" testId="support-header" /><EmptyState icon={<LifeBuoy size={28} />} title="Help Desk module is inactive" description="Enable it in Admin → Modules." /></div>;
  }
  return (
    (<div>
      <PageHeader title="Help Desk & Support" subtitle="Submit and track support tickets, work the queue, and manage configuration" testId="support-header" />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="my" data-testid="support-tab-my"><Ticket size={14} className="mr-1" />{t("My Tickets")}</TabsTrigger>
          {(canScope || hasPerm("support.submit")) && <TabsTrigger value="queue" data-testid="support-tab-queue"><LifeBuoy size={14} className="mr-1" />{t("Queue")}</TabsTrigger>}
          {canScope && <TabsTrigger value="reporting" data-testid="support-tab-reporting"><BarChart3 size={14} className="mr-1" />{t("Reporting")}</TabsTrigger>}
          {canAdmin && <TabsTrigger value="config" data-testid="support-tab-config"><Settings size={14} className="mr-1" />{t("Configuration")}</TabsTrigger>}
          {canAdmin && <TabsTrigger value="access" data-testid="support-tab-access"><ShieldCheck size={14} className="mr-1" />{t("Access")}</TabsTrigger>}
        </TabsList>
        <TabsContent value="my"><TicketList view="my" /></TabsContent>
        <TabsContent value="queue"><TicketList view="queue" /></TabsContent>
        {canScope && <TabsContent value="reporting"><Reporting canExport={canExport} /></TabsContent>}
        {canAdmin && <TabsContent value="config"><ConfigPanel /></TabsContent>}
        {canAdmin && <TabsContent value="access"><AccessPanel /></TabsContent>}
      </Tabs>
    </div>)
  );
}

const QUEUE_VIEWS = ["unassigned", "assigned", "waiting", "at_risk", "breached", "resolved", "reopened"];

const TicketList: React.FC<{ view: string }> = ({ view }) => {
  const {
    t: tI18n
  } = useI18n();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [qview, setQview] = useState(view === "queue" ? "unassigned" : "my");
  const [openId, setOpenId] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    const v = view === "my" ? "my" : qview;
    api.get(`/support/tickets?view=${v}`).then((r) => setRows(r.data.tickets || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [view, qview]);
  useEffect(() => { load(); }, [load]);
  return (
    (<div data-testid={`support-list-${view}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {view === "queue" && (
          <Select value={qview} onValueChange={setQview}>
            <SelectTrigger className="w-48" data-testid="support-queue-view"><SelectValue /></SelectTrigger>
            <SelectContent>{QUEUE_VIEWS.map((v) => <SelectItem key={v} value={v}>{label(v)}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <div className="flex-1" />
        <Button size="sm" onClick={() => setShowNew(true)} data-testid="support-new-ticket-btn"><Plus size={14} className="mr-1" />{tI18n("New ticket")}</Button>
      </div>
      {loading ? <LoadingBlock /> : rows.length === 0 ? (
        <EmptyState icon={<Ticket size={24} />} title="No tickets" description={view === "my" ? tI18n("You have not submitted any tickets yet.") : tI18n("No tickets in this queue view.")} />
      ) : (
        <div className="space-y-2">
          {rows.map((t) => (
            <Card key={t.id} className="flex cursor-pointer items-center justify-between p-3 text-sm hover:bg-muted/40" onClick={() => setOpenId(t.id)} data-testid={`support-ticket-open-${t.id}`}>
              <div>
                <span className="font-medium">#{t.number} · {t.subject}</span>
                <span className="text-xs text-muted-foreground"> · {label(t.category)} · {t.priority} · {label(t.queue_key)}</span>
              </div>
              <div className="flex items-center gap-2">
                {t.breached && <StatusPill status="critical" testId={`support-ticket-breached-${t.number}`}>{tI18n("breached")}</StatusPill>}
                {!t.breached && t.at_risk && <StatusPill status="warn" testId={`support-ticket-atrisk-${t.number}`}>{tI18n("at risk")}</StatusPill>}
                {t.sla_contractual && <StatusPill status="info">SLA</StatusPill>}
                <StatusPill status={STATUS_TONE[t.status] || "info"} testId={`support-ticket-status-${t.number}`}>{label(t.status)}</StatusPill>
              </div>
            </Card>
          ))}
        </div>
      )}
      {showNew && <NewTicketDialog onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {openId && <TicketDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>)
  );
};

const NewTicketDialog: React.FC<{ onClose: () => void; onCreated: () => void }> = ({ onClose, onCreated }) => {
  const {
    t
  } = useI18n();

  const [cfg, setCfg] = useState<any>(null);
  const [stores, setStores] = useState<any[]>([]);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("");
  const [storeId, setStoreId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/support/config/form").then((r) => setCfg(r.data.config)).catch(() => setCfg(null));
    api.get("/org/stores?limit=200").then((r) => setStores(r.data.stores || [])).catch(() => setStores([]));
  }, []);
  const submit = async () => {
    if (!subject || !category || !priority) { toast.error("Subject, category and priority are required"); return; }
    setSaving(true);
    try {
      const res = await api.post("/support/tickets", { subject, description, category, priority, store_id: storeId || null, client_id: `web-${Date.now()}` });
      const tid = res.data.ticket.id;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("client_id", `web-att-${Date.now()}`);
        try { await api.post(`/support/tickets/${tid}/attachments`, fd, { headers: { "Content-Type": undefined } as any }); }
        catch { toast.error("Ticket created, but the attachment was rejected (check file type/size)"); }
      }
      toast.success("Ticket submitted"); onCreated();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed to submit"); } finally { setSaving(false); }
  };
  return (
    (<Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="support-new-dialog">
        <DialogHeader><DialogTitle>{t("New support ticket")}</DialogTitle><DialogDescription>{t("Describe the issue. Suggested articles appear after you submit.")}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">{t("Subject")}</Label><Input className="mt-1" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="support-new-subject" /></div>
          <div><Label className="text-xs">{t("Description")}</Label><Textarea className="mt-1" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="support-new-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">{t("Category")}</Label><Select value={category} onValueChange={setCategory}><SelectTrigger className="mt-1" data-testid="support-new-category"><SelectValue placeholder={t("Select")} /></SelectTrigger><SelectContent>{(cfg?.categories || []).map((c: any) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">{t("Priority")}</Label><Select value={priority} onValueChange={setPriority}><SelectTrigger className="mt-1" data-testid="support-new-priority"><SelectValue placeholder={t("Select")} /></SelectTrigger><SelectContent>{(cfg?.priorities || []).map((p: any) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label className="text-xs">{t("Store (optional)")}</Label><Select value={storeId} onValueChange={setStoreId}><SelectTrigger className="mt-1" data-testid="support-new-store"><SelectValue placeholder={t("No specific store")} /></SelectTrigger><SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{t("Store")}{" "}{s.store_number}· {s.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">{t("Photo / file (optional)")}</Label><Input type="file" className="mt-1" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="support-new-file" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={saving} data-testid="support-new-submit">{t("Submit ticket")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>)
  );
};

const TicketDetail: React.FC<{ id: string; onClose: () => void; onChanged: () => void }> = ({ id, onClose, onChanged }) => {
  const {
    t: tI18n
  } = useI18n();

  const [data, setData] = useState<any>(null);
  const [comment, setComment] = useState("");
  const [internal, setInternal] = useState(false);
  const [sugg, setSugg] = useState<any>(null);
  const [atts, setAtts] = useState<any[]>([]);
  const [escs, setEscs] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const load = useCallback(() => {
    api.get(`/support/tickets/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Failed to load ticket"));
    api.get(`/support/tickets/${id}/suggestions`).then((r) => setSugg(r.data)).catch(() => setSugg(null));
    api.get(`/support/tickets/${id}/attachments`).then((r) => setAtts(r.data.attachments || [])).catch(() => setAtts([]));
    api.get(`/support/tickets/${id}/escalations`).then((r) => setEscs(r.data.escalations || [])).catch(() => setEscs([]));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const t = data?.ticket;
  const canAgent = data?.can_agent;
  const act = async (fn: () => Promise<any>, msg: string) => {
    try { await fn(); toast.success(msg); load(); onChanged(); } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const postComment = () => { if (!comment) return; act(() => api.post(`/support/tickets/${id}/comments`, { body: comment, internal }).then(() => setComment("")), "Comment added"); };
  const ALLOWED: Record<string, string[]> = {
    new: ["triaged", "assigned", "in_progress", "cancelled"], triaged: ["assigned", "in_progress", "cancelled"],
    assigned: ["in_progress", "waiting_on_requester", "waiting_on_third_party", "cancelled"],
    in_progress: ["waiting_on_requester", "waiting_on_third_party", "cancelled"],
    waiting_on_requester: ["in_progress", "cancelled"], waiting_on_third_party: ["in_progress", "cancelled"],
    resolved: ["reopened"], closed: ["reopened"], cancelled: ["reopened"], reopened: ["in_progress", "assigned", "cancelled"],
  };
  const doTransition = (to: string) => {
    let reason: string | null = null;
    if (to === "cancelled" || to === "reopened") { reason = window.prompt(tl("Reason to {action}?", { action: to })) || ""; if (!reason) return; }
    act(() => api.post(`/support/tickets/${id}/transition`, { to, reason }), tl("Moved to {label}", { label: label(to) }));
  };
  const resolve = () => { const summary = window.prompt("Resolution summary?"); if (!summary) return; act(() => api.post(`/support/tickets/${id}/resolve`, { summary, resolution_category: "fixed" }), "Resolved"); };
  const confirm = () => {
    const rating = window.prompt("Rate the resolution 1-5 (optional)");
    const feedback = window.prompt("Any feedback? (optional)") || undefined;
    act(() => api.post(`/support/tickets/${id}/confirm`, { confirmed: true, rating: rating ? parseInt(rating, 10) : undefined, feedback }), "Confirmed");
  };
  const linkAction = () => { const title = window.prompt("Action title?", t?.subject); if (!title) return; act(() => api.post(`/support/tickets/${id}/link-action`, { title, priority: "high" }), "Action linked"); };
  const linkRecord = () => {
    const type = window.prompt("Link type (store_day, task, action, finding, visit, recon_case, shift, time_exception, forecast, knowledge, learning)");
    if (!type) return;
    const rid = window.prompt("Record id?"); if (!rid) return;
    act(() => api.post(`/support/tickets/${id}/links`, { type, record_id: rid }), "Record linked");
  };
  const uploadFile = async (e: React.ChangeEvent<HTMLInputElement>, internalUpload: boolean) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("client_id", `web-${Date.now()}`);
    if (internalUpload) fd.append("internal", "true");
    try {
      await api.post(`/support/tickets/${id}/attachments`, fd, { headers: { "Content-Type": undefined } as any });
      toast.success("Attachment uploaded"); load();
    } catch (err: any) { toast.error(err?.response?.data?.detail || "Upload failed (check file type/size)"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    (<Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="support-detail-dialog">
        {!t ? (
          <>
            <DialogHeader className="sr-only">
              <DialogTitle>{tI18n("Loading ticket")}</DialogTitle>
              <DialogDescription>{tI18n("Please wait while the support ticket loads.")}</DialogDescription>
            </DialogHeader>
            <LoadingBlock />
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle data-testid="support-detail-title">#{t.number} · {t.subject}</DialogTitle>
              <DialogDescription>{label(t.category)}· {t.priority}{" "}{tI18n("· queue")}{" "}{label(t.queue_key)}{t.sla_contractual ? tI18n("· contractual SLA") : ""}</DialogDescription>
            </DialogHeader>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <StatusPill status={STATUS_TONE[t.status] || "info"} testId="support-detail-status">{label(t.status)}</StatusPill>
              <StatusPill status={TARGET_TONE[t.first_response_state]}>{tI18n("first response:")}{" "}{label(t.first_response_state)}</StatusPill>
              <StatusPill status={TARGET_TONE[t.resolution_state]}>{tI18n("resolution:")}{" "}{label(t.resolution_state)}</StatusPill>
            </div>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{t.description}</p>
            {t.resolution && <Card className="p-3 text-sm"><b>{tI18n("Resolution:")}</b> {t.resolution.summary}{t.resolution.root_cause ? <span className="text-muted-foreground">{" "}{tI18n("· root cause:")}{" "}{t.resolution.root_cause}</span> : null}</Card>}
            {typeof t.rating === "number" && <div className="text-xs text-muted-foreground" data-testid="support-detail-feedback">{tI18n("Requester rating:")}{" "}{t.rating}/5{t.feedback ? ` · "${t.feedback}"` : ""}</div>}

            {escs.length > 0 && (
              <Card className="border-amber-300 bg-amber-50 p-2 text-xs" data-testid="support-escalations">
                <span className="font-semibold uppercase text-amber-700">{tI18n("Escalations")}</span>
                {escs.map((e) => (
                  <div key={e.id} className="mt-1 flex items-center justify-between">
                    <span>{label(e.target)}{" "}{tI18n("target")}{" "}{e.when}{" "}{tI18n("· notified")}{" "}{e.recipients?.length || 0}{" "}{tI18n("· external:")}{" "}{e.delivery?.external}</span>
                    <span className="flex items-center gap-2">
                      <StatusPill status={e.status === "open" ? "warn" : "good"}>{stat(e.status)}</StatusPill>
                      {canAgent && e.status === "open" && <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/support/escalations/${e.id}/ack`), "Acknowledged")} data-testid={`support-esc-ack-${e.id}`}>{tI18n("Ack")}</Button>}
                    </span>
                  </div>
                ))}
              </Card>
            )}

            {/* actions */}
            <div className="flex flex-wrap gap-2">
              {canAgent && (ALLOWED[t.status] || []).filter((s) => !["resolved"].includes(s)).map((s) => (
                <Button key={s} size="sm" variant="outline" onClick={() => doTransition(s)} data-testid={`support-transition-${s}`}>{label(s)}</Button>
              ))}
              {canAgent && ["assigned", "in_progress", "waiting_on_requester", "waiting_on_third_party", "reopened"].includes(t.status) && (
                <Button size="sm" onClick={resolve} data-testid="support-resolve-btn">{tI18n("Resolve")}</Button>
              )}
              {canAgent && <Button size="sm" variant="outline" onClick={linkAction} data-testid="support-link-action-btn">{tI18n("Link Action")}</Button>}
              {canAgent && <Button size="sm" variant="outline" onClick={linkRecord} data-testid="support-link-record-btn">{tI18n("Link Record")}</Button>}
              {canAgent && t.store_id && <Button size="sm" variant="outline" onClick={() => { const into = window.prompt("Merge into ticket id?"); if (into) act(() => api.post(`/support/tickets/${id}/merge`, { into_ticket_id: into, reason: "duplicate" }), "Merged"); }} data-testid="support-merge-btn">{tI18n("Merge")}</Button>}
              {!canAgent && t.status === "resolved" && <Button size="sm" onClick={confirm} data-testid="support-confirm-btn">{tI18n("Confirm resolution")}</Button>}
              {!canAgent && !["closed", "cancelled"].includes(t.status) && <Button size="sm" variant="outline" onClick={() => doTransition("cancelled")} data-testid="support-cancel-btn">{tI18n("Cancel ticket")}</Button>}
            </div>

            {(t.links && t.links.length > 0) && (
              <div className="text-xs text-muted-foreground" data-testid="support-links">{tI18n("Linked records:")}{" "}{t.links.map((l: any) => `${label(l.type)}`).join(", ")}</div>
            )}

            {/* attachments */}
            <div className="space-y-2" data-testid="support-attachments">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase text-muted-foreground">{tI18n("Attachments")}</span>
                <label className="cursor-pointer text-xs text-primary" data-testid="support-attach-upload">
                  {uploading ? tI18n("Uploading…") : tI18n("+ Add photo/file")}
                  <input type="file" className="hidden" onChange={(e) => uploadFile(e, false)} disabled={uploading} />
                </label>
              </div>
              {atts.length === 0 ? <div className="text-xs text-muted-foreground">{tI18n("No attachments.")}</div> : atts.map((a) => (
                <a key={a.id} href={`${(process.env.REACT_APP_BACKEND_URL as string)}/api/support/attachments/${a.id}`} target="_blank" rel="noreferrer" className="block text-xs text-primary" data-testid={`support-attachment-${a.id}`}>
                  📎 {a.filename} {a.internal ? tI18n("(internal)") : ""}
                </a>
              ))}
              {canAgent && (
                <label className="cursor-pointer text-xs text-amber-700" data-testid="support-attach-internal">{tI18n("+ Add internal attachment")}<input type="file" className="hidden" onChange={(e) => uploadFile(e, true)} disabled={uploading} />
                </label>
              )}
            </div>


            {/* comments */}
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase text-muted-foreground">{tI18n("Conversation")}</div>
              {(data.comments || []).map((c: any, i: number) => (
                <div key={i} className={`rounded p-2 text-sm ${c.kind === "internal" ? "border border-amber-300 bg-amber-50" : "bg-muted/40"}`} data-testid={`support-comment-${c.kind}`}>
                  {c.kind === "internal" && <span className="mr-1 text-[10px] font-semibold uppercase text-amber-600">{tI18n("Internal")}</span>}
                  {c.body}
                </div>
              ))}
              <Textarea placeholder={tI18n("Add a comment…")} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="support-comment-input" />
              <div className="flex items-center justify-between">
                {canAgent ? (
                  <label className="flex items-center gap-2 text-xs"><Switch checked={internal} onCheckedChange={setInternal} data-testid="support-comment-internal-toggle" />{tI18n("Internal note (not visible to requester)")}</label>
                ) : <span />}
                <Button size="sm" onClick={postComment} data-testid="support-comment-submit">{tI18n("Send")}</Button>
              </div>
            </div>

            {/* suggestions */}
            {sugg && (sugg.knowledge?.length > 0 || sugg.similar_tickets?.length > 0) && (
              <Card className="p-3 text-sm" data-testid="support-suggestions">
                <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">{tI18n("Suggested help")}</div>
                {(sugg.knowledge || []).map((k: any) => <div key={k.id} className="text-xs">📄 {k.title}</div>)}
                {(sugg.similar_tickets || []).map((s: any) => <div key={s.id} className="text-xs text-muted-foreground">#{s.number} {s.subject}{s.resolution_summary ? ` — ${s.resolution_summary}` : ""}</div>)}
              </Card>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>)
  );
};

const Reporting: React.FC<{ canExport: boolean }> = ({ canExport }) => {
  const {
    t
  } = useI18n();

  const [rep, setRep] = useState<any>(null);
  useEffect(() => { api.get("/support/reporting").then((r) => setRep(r.data)).catch(() => setRep(null)); }, []);
  if (!rep) return <LoadingBlock />;
  if (!rep.available) return <EmptyState icon={<BarChart3 size={24} />} title="Reporting unavailable" description="Help Desk is not active." />;
  const Stat = ({ k, v }: { k: string; v: any }) => (
    <Card className="p-3" data-testid={`support-stat-${k}`}><div className="text-xs text-muted-foreground">{k}</div><div className="text-xl font-semibold">{v ?? "—"}</div></Card>
  );
  return (
    (<div data-testid="support-reporting">
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat k="Total" v={rep.total} />
        <Stat k="At risk" v={rep.at_risk} />
        <Stat k="Breached" v={rep.breached} />
        <Stat k="Reopen rate" v={rep.reopen_rate} />
        <Stat k="Avg first response (min)" v={rep.avg_first_response_min} />
        <Stat k="Avg resolution (min)" v={rep.avg_resolution_min} />
      </div>
      <Card className="p-3 text-sm">
        <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">{t("By status")}</div>
        <div className="flex flex-wrap gap-2">{Object.entries(rep.by_status || {}).map(([k, v]: any) => <StatusPill key={k} status={STATUS_TONE[k] || "info"}>{label(k)}: {v}</StatusPill>)}</div>
      </Card>
      {canExport && <Button className="mt-3" size="sm" variant="outline" onClick={() => window.open(`${(process.env.REACT_APP_BACKEND_URL as string)}/api/support/export`, "_blank")} data-testid="support-export-btn">{t("Export CSV")}</Button>}
    </div>)
  );
};

const ConfigPanel: React.FC = () => {
  const {
    t: tI18n
  } = useI18n();

  const [cfg, setCfg] = useState<any>(null);
  useEffect(() => { api.get("/support/config").then((r) => setCfg(r.data)).catch(() => setCfg(null)); }, []);
  if (!cfg) return <LoadingBlock />;
  const a = cfg.active;
  return (
    (<div data-testid="support-config">
      {!a ? <EmptyState icon={<Settings size={24} />} title="No configuration" description="No active Help Desk configuration." /> : (
        <div className="space-y-3">
          <Card className="p-3 text-sm">
            <div className="flex items-center justify-between"><b>{tI18n("Active configuration v")}{a.version}</b><StatusPill status="good">{tI18n("active")}</StatusPill></div>
            <div className="mt-2 text-xs text-muted-foreground">{tI18n("Categories:")}{" "}{(a.categories || []).map((c: any) => c.name).join(", ")}</div>
            <div className="text-xs text-muted-foreground">{tI18n("Priorities:")}{" "}{(a.priorities || []).map((p: any) => p.name).join(", ")}</div>
            <div className="text-xs text-muted-foreground">{tI18n("Queues:")}{" "}{(a.queues || []).map((q: any) => `${q.name} (${q.timezone})`).join(", ")}</div>
            <div className="text-xs text-muted-foreground">{tI18n("Service targets:")}{" "}{(a.service_targets || []).length}{" "}{tI18n("defined")}{(a.service_targets || []).some((t: any) => t.contractual) ? tI18n("· includes contractual SLA") : ""}</div>
          </Card>
          <div className="text-xs font-medium uppercase text-muted-foreground">{tI18n("Version history")}</div>
          {(cfg.versions || []).map((v: any) => (
            <Card key={v.id} className="flex items-center justify-between p-2 text-sm" data-testid={`support-config-version-${v.version}`}>
              <span>v{v.version} · {v.reason || "—"}</span>
              <StatusPill status={v.active ? "good" : "inactive"}>{stat(v.status)}</StatusPill>
            </Card>
          ))}
          <p className="text-xs text-muted-foreground">{tI18n(
            "Activating a new version supersedes the current one; tickets keep the version that governed them."
          )}</p>
        </div>
      )}
    </div>)
  );
};

const AccessPanel: React.FC = () => {
  const {
    t
  } = useI18n();

  const [agents, setAgents] = useState<any[]>([]);
  const [access, setAccess] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [agentUser, setAgentUser] = useState("");const [agentReason, setAgentReason] = useState("");
  const load = useCallback(() => {
    api.get("/support/agent-grants").then((r) => setAgents(r.data.grants || [])).catch(() => setAgents([]));
    api.get("/support/access-grants").then((r) => setAccess(r.data.grants || [])).catch(() => setAccess([]));
    api.get("/admin/users?limit=200").then((r) => setUsers(r.data.users || [])).catch(() => setUsers([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const addAgent = async () => {
    if (!agentUser) { toast.error("Select a user"); return; }
    try { await api.post("/support/agent-grants", { user_id: agentUser, queue_key: "all", reason: agentReason }); toast.success("Agent granted"); setAgentUser(""); setAgentReason(""); load(); } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const revoke = async (kind: string, id: string) => { try { await api.post(`/support/${kind}/${id}/revoke`); toast.success("Revoked"); load(); } catch { toast.error("Failed"); } };
  const uname = (uid: string) => { const u = users.find((x) => x.id === uid); return u ? (u.name || u.email) : uid?.slice(-6); };
  return (
    (<div className="space-y-4" data-testid="support-access">
      <Card className="p-3">
        <div className="mb-2 text-sm font-medium">{t("Support agents (grant-gated per tenant)")}</div>
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <div><Label className="text-xs">{t("User")}</Label><Select value={agentUser} onValueChange={setAgentUser}><SelectTrigger className="mt-1 w-56" data-testid="support-agent-user"><SelectValue placeholder={t("Select user")} /></SelectTrigger><SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}</SelectContent></Select></div>
          <Input placeholder={t("Reason")} value={agentReason} onChange={(e) => setAgentReason(e.target.value)} className="w-48" data-testid="support-agent-reason" />
          <Button size="sm" onClick={addAgent} data-testid="support-agent-grant-btn">{t("Grant agent")}</Button>
        </div>
        <div className="space-y-1">{agents.filter((g) => g.active).map((g) => (
          <div key={g.id} className="flex items-center justify-between text-sm" data-testid={`support-agent-grant-${g.id}`}>
            <span>{uname(g.user_id)}{" "}{t("· queue")}{" "}{g.queue_key}· {g.reason || "—"}</span>
            <Button size="sm" variant="outline" onClick={() => revoke("agent-grants", g.id)}>{t("Revoke")}</Button>
          </div>
        ))}</div>
      </Card>
      <Card className="p-3">
        <div className="mb-1 flex items-center gap-2 text-sm font-medium"><AlertTriangle size={14} className="text-amber-500" />{t("WarRoom platform support access")}</div>
        <p className="mb-2 text-xs text-muted-foreground">{t(
          "Platform support has no automatic access. Any access must be explicitly granted, time-bounded, reasoned and revocable — every grant is audited."
        )}</p>
        <div className="space-y-1">{access.filter((g) => g.active).length === 0 ? <div className="text-xs text-muted-foreground">{t("No active platform access grants.")}</div> : access.filter((g) => g.active).map((g) => (
          <div key={g.id} className="flex items-center justify-between text-sm" data-testid={`support-access-grant-${g.id}`}>
            <span>{uname(g.user_id)}{" "}{t("· until")}{" "}{fmtDate(g.expires_at)}· {g.reason}</span>
            <Button size="sm" variant="outline" onClick={() => revoke("access-grants", g.id)}>{t("Revoke")}</Button>
          </div>
        ))}</div>
      </Card>
    </div>)
  );
};
